import { useEffect } from 'react';
import { message, notification } from 'antd';
import { userActions } from 'src/actions.js';
import {
	peekRedirectURL,
	rememberRedirectURL,
} from 'src/_shared/services/redirectAfterLogin.js';
import { isMobileShell, postToShell } from 'src/_shared/services/utils.js';
import {
	beginShellHandoff,
	isHandingOff,
	rememberShellHome,
	takeShellHandoffEmail,
	withShellParams,
} from 'src/_shared/shell.js';

export default function LogoutComponent(props) {
	useEffect(() => {
		const asyncSignOut = async () => {
			// AuthenticatedRoute sends users here (with the page they asked for)
			// when a session exists without a user record. Keep that destination
			// so the login flow can finish the trip. Explicit sign-outs carry no
			// state, and the API layer stores the current page itself before it
			// navigates here. (ERIN-8413)
			const requestedURL = props.location?.state?.redirectURL;
			if (requestedURL) rememberRedirectURL(requestedURL);

			const { signOut } = userActions;
			signOut();
			// This handleSignOut comes from Api Provider -> app component -> app router -> logout component
			// To prevent race conditions, don't call handleSignOut from other components, a redirect to /logout is all that's needed
			await props.handleSignOut();

			// Let the mobile shell drop its push tags for this user (no-op in a
			// browser).
			postToShell({ type: 'erin:signout' });

			// The user's company has moved to the eAI portal (ShellHandoff): with
			// the legacy session gone, point the mobile shell at the portal. It
			// remembers the portal as this install's home and reloads there.
			// Done before the cross-tab broadcast below, whose own listener in
			// this page reloads /logout; the shell has no other tabs anyway.
			const handoff = props.location?.state?.handoff;
			if (handoff?.url && isHandingOff()) return; // a second mount while leaving
			if (handoff?.url) {
				// Requests still in flight from the page we left fail once the
				// session is gone; their toasts have nothing to tell a user who
				// is on the way to the portal. Hide any that are up or still to
				// come (the class is styled in _shell.scss) until the page unloads.
				beginShellHandoff();
				message.destroy();
				notification.destroy();
				// After the clear above, so the next launch of a shell with no
				// memory of its own (Capacitor, TWA) starts on the portal.
				rememberShellHome(handoff);
				if (
					postToShell({
						type: 'erin:home',
						url: handoff.url,
						companyId: handoff.companyId,
					})
				) {
					// Leave /logout (and its state) behind so nothing re-runs the
					// sign-out while the shell swaps the page.
					props.history.replace('/login');
				} else {
					// Carry the signed-in email so the portal's login continues to
					// the PIN step on its own.
					window.location.assign(
						withShellParams(handoff.url, {
							email: takeShellHandoffEmail() || handoff.email,
						})
					);
				}

				return;
			}

			// Broadcast sign-out to other tabs, but only if this tab initiated it.
			// Not in the mobile shell: it has a single page, and the broadcast
			// echoes back into this page's own listener, which reloads /logout
			// mid sign-out. In the WebView that reload can stall on the half-
			// cleared IndexedDB cache and leave the user on a spinner. (ERIN-8561)
			if (!sessionStorage.getItem('remote-sign-out') && !isMobileShell()) {
				const bc = new BroadcastChannel('erin-user-sync');
				bc.postMessage({ type: 'SIGN_OUT' });
				bc.close();
			}

			sessionStorage.removeItem('remote-sign-out');

			// handleSignOut wipes localStorage but not sessionStorage, so a
			// destination stored before the sign-out survives. Send the user to
			// the login page with it instead of the root so SSO and password
			// logins both land where they were originally headed.
			const redirectURL = peekRedirectURL();
			if (redirectURL) {
				props.history.push({ pathname: '/login', state: { redirectURL } });
			} else {
				props.history.push('/');
			}
		};

		asyncSignOut();
	}, []);

	return <></>; // The ApiProvider will render a spinner, this component has no rendering
}
